import React, { Fragment, FunctionComponent, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  requiredValidation,
  onlyLetters,
} from 'helpers/validations';
import { getCountries } from 'helpers/countries';
import FieldRequired from 'components/commons/FieldRequired';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import Checkbox from 'components/inputs/Checkbox';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IJewishnessFormData, IJewishnessFormProps } from './types';
import './i18n';

const JewishnessForm: FunctionComponent<IJewishnessFormProps> = (props: IJewishnessFormProps) => {
  const {
    cementeries,
    civilStates,
    files,
    idCardTypes,
    initialValues,
    isEditing,
    onSubmit,
    tribes,
  } = props;
  const [formValues, setFormValues] = useState<IJewishnessFormData>(initialValues);
  const countries = useMemo(() => getCountries(), []);

  const tabs = [
    i18next.t<string>('JEWISHNESS_FORM:PERSON'),
    ...(formValues.person.civilState === 'MARRIED'
      ? [i18next.t<string>('JEWISHNESS_FORM:SPOUSE')]
      : []),
    i18next.t<string>('JEWISHNESS_FORM:PARENTS'),
    i18next.t<string>('JEWISHNESS_FORM:GRANDPARENTS'),
    i18next.t<string>('JEWISHNESS_FORM:WITNESSES'),
    i18next.t<string>('JEWISHNESS_FORM:COMMENTS'),
  ];

  const today = new Date().toISOString().split('T')[0];

  return (
    <ViewContainer auth data-testid="jewishness-form">
      <Title>{i18next.t<string>('JEWISHNESS_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IJewishnessFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="person-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:LAST_NAME')}
                name="person.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:FIRST_NAME')}
                name="person.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                name="person.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD')}
                name="person.idCard"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                name="person.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                    value: files?.personIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                name="person.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:COUNTRY')}
                name="person.country"
                options={countries}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:AREA_CODE')}
                  maxLength={4}
                  name="person.areaCode"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:PHONE')}
                  maxLength={8}
                  name="person.phone"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:EMAIL')}
                name="person.email"
                render={Input}
                type="email"
                validate={composeValidators(requiredValidation, emailValidation)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:COMMUNITY')}
                name="person.community"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                name="person.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:TRIBE')}
                name="person.tribe"
                options={tribes}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:CIVIL_STATE')}
                name="person.civilState"
                options={civilStates}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTH_CERTIFICATE_FILE')}
                name="person.birthCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:BIRTH_CERTIFICATE_FILE'),
                    value: files?.personBirthCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              {['MARRIED', 'DIVORCED'].includes(`${formValues.person.civilState}`) && (
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:WEDDING_PLACE')}
                  name="person.weddingPlace"
                  render={Input}
                  validate={requiredValidation}
                />
              )}
              {formValues.person.civilState === 'DIVORCED' && (
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:GET_FILE')}
                  name="person.getFile"
                  previewFiles={[
                    {
                      label: i18next.t<string>('JEWISHNESS_FORM:GET_FILE'),
                      value: files?.personGetFile,
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              )}
            </FormRowContainer>
          </MultiStepForm.Page>
          {formValues.person.civilState === 'MARRIED' && (
            <MultiStepForm.Page key="spouse-page">
              <Typography variant="h6">
                {i18next.t<string>('JEWISHNESS_FORM:SPOUSE_INFORMATION')}
              </Typography>
              <br />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:LAST_NAME')}
                  name="spouse.lastName"
                  parse={(value: string) => value}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:FIRST_NAME')}
                  name="spouse.firstName"
                  parse={(value: string) => value}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                  name="spouse.idCardType"
                  options={idCardTypes}
                  parse={(value: string) => value}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD')}
                  name="spouse.idCard"
                  parse={(value: string) => value}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                  name="spouse.idCardFile"
                  previewFiles={[
                    {
                      label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                      value: files?.spouseIdCardFile,
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                  name="spouse.idCardBackFile"
                  previewFiles={[
                    {
                      label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                      value: files?.spouseIdCardBackFile,
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              </FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:COUNTRY')}
                name="spouse.country"
                options={countries}
                parse={(value: string) => value}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                  name="spouse.birthdate"
                  parse={(value: string) => value}
                  render={Input}
                  type="date"
                  max={today}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('JEWISHNESS_FORM:TRIBE')}
                  name="spouse.tribe"
                  parse={(value: string) => value}
                  options={tribes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:EMAIL')}
                  name="spouse.email"
                  parse={(value: string) => value}
                  render={Input}
                  type="email"
                  validate={composeValidators(requiredValidation, emailValidation)}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:COMMUNITY')}
                  name="spouse.community"
                  parse={(value: string) => value}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('JEWISHNESS_FORM:ALIVE')}
                  name="spouse.alive"
                  render={Checkbox}
                />
                {!formValues.spouse.alive && (
                  <Field
                    label={i18next.t<string>('JEWISHNESS_FORM:CEMENTERY')}
                    name="spouse.cementery"
                    options={cementeries}
                    parse={(value: string) => value}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                )}
              </FormRowContainer>
            </MultiStepForm.Page>
          )}
          <MultiStepForm.Page key="parents-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_PARENTS_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:FATHER_LAST_NAME')}
                name="person.parents.father.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MOTHER_LAST_NAME')}
                name="person.parents.mother.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:FATHER_FIRST_NAME')}
                name="person.parents.father.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MOTHER_FIRST_NAME')}
                name="person.parents.mother.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                name="person.parents.father.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                name="person.parents.mother.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD')}
                name="person.parents.father.idCard"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD')}
                name="person.parents.mother.idCard"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                name="person.parents.father.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                    value: files?.personParentsFatherIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                name="person.parents.mother.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                    value: files?.personParentsMotherIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                name="person.parents.father.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personParentsFatherIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                name="person.parents.mother.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personParentsMotherIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                name="person.parents.father.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                name="person.parents.mother.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:WEDDING_PLACE')}
                name="person.parents.weddingPlace"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:WEDDING_DATE')}
                name="person.parents.weddingDate"
                render={Input}
                type="date"
                max={today}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.parents.ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personParentsKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.parents.marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personParentsMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="grand-parents-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_GRANDPARENTS_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_FATHER_LAST_NAME')}
                name="person.grandParents.paternalFather.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_FATHER_FIRST_NAME')}
                name="person.grandParents.paternalFather.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.paternalFather.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_MOTHER_LAST_NAME')}
                name="person.grandParents.paternalMother.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_MOTHER_FIRST_NAME')}
                name="person.grandParents.paternalMother.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.paternalMother.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.grandParents.paternalKetubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personGrandParentsPaternalKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.grandParents.paternalMarriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personGrandParentsPaternalMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE')}
              name="person.grandParents.paternalCemeteryBurialCertificateFile"
              previewFiles={[
                {
                  label: i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE'),
                  value: files?.personGrandParentsPaternalCemeteryBurialCertificateFile,
                },
              ]}
              render={Input}
              type="file"
              validate={fileValidation}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_FATHER_LAST_NAME')}
                name="person.grandParents.maternalFather.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_FATHER_FIRST_NAME')}
                name="person.grandParents.maternalFather.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.maternalFather.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_MOTHER_LAST_NAME')}
                name="person.grandParents.maternalMother.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_MOTHER_FIRST_NAME')}
                name="person.grandParents.maternalMother.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.maternalMother.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.grandParents.maternalKetubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personGrandParentsMaternalKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.grandParents.maternalMarriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personGrandParentsMaternalMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE')}
              name="person.grandParents.maternalCemeteryBurialCertificateFile"
              previewFiles={[
                {
                  label: i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE'),
                  value: files?.personGrandParentsMaternalCemeteryBurialCertificateFile,
                },
              ]}
              render={Input}
              type="file"
              validate={fileValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="witnesses-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_WITNESSES_INFORMATION')}
            </Typography>
            <br />
            {(formValues.person?.witnesses as Array<unknown>)?.map(
              (_witness: unknown, i: number) => (
                <Fragment key={`witness-${i.toString()}`}>
                  <FieldRequired
                    label={`${i18next.t<string>('JEWISHNESS_FORM:WITNESS_NAME')} ${i + 1}`}
                    name={`person.witnesses[${i}].name`}
                    parse={(v: string) => v}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                      name={`person.witnesses[${i}].idCardType`}
                      options={idCardTypes}
                      parse={(v) => v}
                      render={SelectInput}
                    />
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD')}
                      name={`person.witnesses[${i}].idCard`}
                      parse={(v) => v}
                      render={Input}
                    />
                  </FormRowContainer>
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                      name={`person.witnesses[${i}].idCardFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                          value: files?.personWitnessesIdCardFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                      name={`person.witnesses[${i}].idCardBackFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                          value: files?.personWitnessesIdCardBackFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                  </FormRowContainer>
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ADDRESS')}
                      name={`person.witnesses[${i}].address`}
                      parse={(v) => v}
                      render={Input}
                    />
                    <FormRowContainer>
                      <Field
                        label={i18next.t<string>('JEWISHNESS_FORM:AREA_CODE')}
                        maxLength={4}
                        name={`person.witnesses[${i}].areaCode`}
                        parse={(v) => digitsOnly(v)}
                        render={Input}
                      />
                      <Field
                        label={i18next.t<string>('JEWISHNESS_FORM:PHONE')}
                        maxLength={8}
                        name={`person.witnesses[${i}].phone`}
                        parse={(v) => digitsOnly(v)}
                        render={Input}
                      />
                    </FormRowContainer>
                  </FormRowContainer>
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                      name={`person.witnesses[${i}].ketubaFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                          value: files?.personWitnessesKetubaFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                      name={`person.witnesses[${i}].marriageCertificateFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                          value: files?.personWitnessesMarriageCertificateFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                  </FormRowContainer>
                </Fragment>
              ),
            )}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('JEWISHNESS_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default JewishnessForm;
